import type { RouteLocationRaw } from 'vue-router'

import type { IconNameType } from '@/components/Icon'
import { YOUTUBE_URL } from '@/constants/general'
import type { TR } from '@/i18n/i18n-global'
import type { LinkTarget } from '@/types'

import { ROUTE_NAMES } from './constants'

// external routes not handled by vue-router
export const DEVELOPERS_LANDPAGE_PATH = '/developers'
export const BLOG_PATH = '/blog/'
export const BUSINESS_LANDPAGE_PATH = '/business'

export type NavLink = {
    // TODO: type title, link to safely use our routes/route-names
    title: string
    to?: RouteLocationRaw
    externalTo?: string
    target?: LinkTarget
}

export const PRIVACY_POLICY_NAVIGATION_ITEM = (tr: TR): NavLink => ({
    title: tr('navigation.privacy_policy'),
    to: { name: ROUTE_NAMES.CONFIDENTIAL },
})

export const ABOUT_US_NAVIGATION_ITEM = (tr: TR): NavLink => ({
    title: tr('navigation.about_us'),
    to: { path: '/about-us/' },
    target: '_blank',
})

// Organization.sameAs source, not the footer socials - identity links (Wikidata first) belong here too
export const RAW_SOCIAL_LINKS = [
    'https://www.wikidata.org/wiki/Q140768339',
    'https://www.linkedin.com/company/playgama',
    'https://www.instagram.com/playgama/',
    'https://www.facebook.com/playgamagames',
    'https://x.com/PlaygamaCom',
    'https://bsky.app/profile/playgama.bsky.social',
    YOUTUBE_URL,
    'https://www.trustpilot.com/review/playgama.com',
    'https://www.crunchbase.com/organization/playgama',
    'https://github.com/playgama',
    'https://www.tiktok.com/@playgama.com',
    'https://t.me/playgama_bridge',
    'https://pitchbook.com/profiles/company/711866-62',
    'https://playgama.itch.io',
]

export const SOCIAL_LINKS = (tr: TR): (NavLink & { icon: IconNameType })[] => [
    {
        title: tr('socials.playgamaForDevs'),
        icon: 'discord',
        externalTo: 'https://discord.com/invite/pzqd2upxr8',
        target: '_blank',
    },
    {
        title: 'Playgama Bridge',
        icon: 'telegram',
        externalTo: 'https://t.me/playgama_bridge',
        target: '_blank',
    },
    {
        title: 'LinkedIn',
        icon: 'linked-in',
        externalTo: 'https://www.linkedin.com/company/playgama',
        target: '_blank',
    },
    {
        title: 'Facebook',
        icon: 'facebook',
        externalTo: 'https://www.facebook.com/playgamagames',
        target: '_blank',
    },
    {
        title: 'X.com (ex-Twitter)',
        icon: 'twitter',
        externalTo: 'https://x.com/PlaygamaCom',
        target: '_blank',
    },
    {
        title: 'Bluesky',
        icon: 'bluesky',
        externalTo: 'https://bsky.app/profile/playgama.bsky.social',
        target: '_blank',
    },
    {
        title: 'Github',
        icon: 'github',
        externalTo: 'https://github.com/playgama/bridge',
        target: '_blank',
    },
]

export const NAVIGATION_LINKS = (tr: TR): NavLink[] => [
    ABOUT_US_NAVIGATION_ITEM(tr),
    {
        title: tr('navigation.for_developers'),
        externalTo: DEVELOPERS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('navigation.for_business'),
        externalTo: BUSINESS_LANDPAGE_PATH,
        target: '_blank',
    },
    PRIVACY_POLICY_NAVIGATION_ITEM(tr),
    {
        title: tr('navigation.contact_us'),
        to: { name: ROUTE_NAMES.CONTACTS },
    },
    {
        title: tr('navigation.takedown_notice'),
        to: { name: ROUTE_NAMES.TAKEDOWN_NOTICE },
    },
    {
        title: tr('navigation.terms_of_use'),
        to: { name: ROUTE_NAMES.TERMS_OF_USE },
    },
    {
        title: tr('navigation.digital_service_act'),
        to: { name: ROUTE_NAMES.DIGITAL_SERVICE_ACT },
    },
]

export const NAVIGATION_ALL_GAMES_LINKS = (tr: TR) =>
    ({
        allGames: {
            title: tr('navigation.all_games'),
            icon: 'all_games',
            to: { name: ROUTE_NAMES.ALL_CATEGORIES },
        },
        allSeries: {
            title: tr('navigation.all_series'),
            icon: 'all_series',
            to: { name: ROUTE_NAMES.ALL_SERIES },
        },
        allTags: {
            title: tr('navigation.all_tags'),
            icon: 'all_tags',
            to: { name: ROUTE_NAMES.ALL_TAGS },
        },
    }) as const
